import { Box, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ─── Palette — restrained, clinical, professional. No dark/navy sections. ────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

const FEATURES = [
  {
    icon: MenuBookOutlinedIcon,
    tag: "Disease Education",
    title: "Understand your diagnosis",
    body: "Clear, clinically accurate explanations of each CKD cause — diabetic nephropathy, hypertensive nephrosclerosis, glomerulonephritis, and polycystic kidney disease — with stage-by-stage guidance and current treatment summaries.",
  },
  {
    icon: ScienceOutlinedIcon,
    tag: "Clinical Trials",
    title: "Find trials enrolling now",
    body: "Actively enrolling studies with direct links to ClinicalTrials.gov and sponsor sites. Filter by CKD etiology, stage, and dialysis status.",
  },
  {
    icon: AssessmentOutlinedIcon,
    tag: "Stage Calculator",
    title: "Know your CKD stage",
    body: "Enter your eGFR and urine albumin-to-creatinine ratio (UACR) to see your KDIGO stage and risk category — the same criteria your nephrologist uses.",
  },
  {
    icon: LocalHospitalOutlinedIcon,
    tag: "Specialist Care",
    title: "Reach expert care",
    body: "Find nephrology practices and dialysis centers near you with NephroTrax's provider finder, and connect with a specialist who treats your specific CKD cause.",
  },
];

export default function EducationLanding() {
  const navigate = useNavigate();

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
      `}</style>

      {/* ══════════════════════════════════════════════════════════════
          HERO — "What is chronic kidney disease?" — the natural
          entry point for the Learn section.
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 6, md: 9 }, pb: { xs: 6, md: 8 }, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ maxWidth: 780 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
            Disease Education
          </Typography>

          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "2.1rem", md: "3rem" }, fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.01em", color: INK, mb: 0.5 }}>
            What is chronic kidney disease?
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontStyle: "italic", fontSize: { xs: "1.15rem", md: "1.4rem" }, fontWeight: 400, color: ACCENT, mb: 3 }}>
            A gradual decline in kidney function.
          </Typography>

          <Typography sx={{ fontSize: { xs: "0.98rem", md: "1.05rem" }, fontWeight: 400, color: SLATE, maxWidth: 560, lineHeight: 1.7, mb: 4 }}>
            Chronic kidney disease (CKD) occurs when the kidneys are gradually damaged and lose their ability to
            filter waste and excess fluid from the blood. It's classified into five stages based on eGFR, and can
            stem from several underlying causes — the cause and stage together determine treatment and outlook.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 4.5 }}>
            {[
              { label: "5 main causes", path: "/education/types" },
              { label: "5 CKD stages", path: "/education/basics" },
              { label: "Slowable — if caught early", path: "/education/treatment" },
            ].map((badge) => (
              <Box
                key={badge.label}
                onClick={() => navigate(badge.path)}
                sx={{
                  px: 2,
                  py: 0.75,
                  bgcolor: ACCENT_SOFT,
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  "&:hover": { bgcolor: "#A7F3D0" },
                }}
              >
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: ACCENT }}>{badge.label}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              onClick={() => navigate("/education/types")}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF" } }}
            >
              Explore CKD causes
            </Button>
            <Button
              onClick={() => navigate("/trials/browse")}
              sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", border: "1px solid #A7F3D0", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF" } }}
            >
              Browse clinical trials
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          FEATURES — plain bordered cards, line icons, no emoji
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 7, md: 10 }, bgcolor: PAPER }}>
        <Box sx={{ maxWidth: 1180, mx: "auto" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 1.5 }}>
            What you'll find here
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.6rem", md: "2.1rem" }, fontWeight: 600, color: INK, mb: 6, maxWidth: 560 }}>
            Everything you need to understand your kidney disease
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {FEATURES.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <Box key={i} sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", p: { xs: 3, md: 3.5 }, bgcolor: PAPER }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "8px", bgcolor: ACCENT_SOFT, display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5 }}>
                    <Icon sx={{ fontSize: 21, color: ACCENT }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED, mb: 1 }}>
                    {feat.tag}
                  </Typography>
                  <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: "1.2rem", fontWeight: 600, color: INK, mb: 1.25 }}>
                    {feat.title}
                  </Typography>
                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 400, color: SLATE, lineHeight: 1.65 }}>
                    {feat.body}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          CLOSING CTA
      ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 7, md: 9 }, bgcolor: CANVAS }}>
        <Box
          sx={{
            maxWidth: 780,
            mx: "auto",
            textAlign: "center",
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            bgcolor: PAPER,
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 6 },
          }}
        >
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.5rem", md: "1.9rem" }, fontWeight: 600, color: INK, mb: 2 }}>
            Ready to learn more?
          </Typography>
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 400, color: SLATE, mb: 4, maxWidth: 480, mx: "auto", lineHeight: 1.7 }}>
            Start with CKD causes, or find clinical trials enrolling near you.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              onClick={() => navigate("/education/types")}
              sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF" } }}
            >
              CKD Causes
            </Button>
            <Button
              onClick={() => navigate("/trials/browse")}
              sx={{ fontWeight: 500, fontSize: "0.92rem", px: 3.25, py: 1.4, bgcolor: "#A7F3D0", color: "#065F46", border: "1px solid #A7F3D0", borderRadius: "6px", textTransform: "none", "&:hover": { bgcolor: "#065F46", color: "#FFFFFF", borderColor: "#065F46" } }}
            >
              Browse Trials
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}